import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { useSrfUpload } from '../model/use-srf-upload';
import { SrfFileInput } from './srf-file-input';

interface SrfUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SrfUploadModal({ isOpen, onClose }: SrfUploadModalProps) {
  const { t } = useTranslation();
  const {
    file,
    errorMessage,
    isLoading,
    handleFileSelect,
    handleFileError,
    handleRemoveFile,
    handleSubmit,
    resetForm
  } = useSrfUpload({
    onSuccess: onClose
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('newcmr.uploadsrf.label', 'Upload SRF form')}</DialogTitle>
          <DialogDescription>
            {t('srf.upload.description', 'Select an .xlsx SRF file to create a new consignment.')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <SrfFileInput
            file={file}
            onFileSelect={handleFileSelect}
            onFileError={handleFileError}
            onFileRemove={handleRemoveFile}
            disabled={isLoading}
          />

          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <ActionButton type="button" intent="dismiss" onClick={handleClose} disabled={isLoading}>
            {t('gen.cancel.label', 'Close Dialog')}
          </ActionButton>
          <ActionButton intent="primary" type="button" onClick={handleSubmit} disabled={!file || isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('newcmr.uploadsrf.label', 'Upload SRF form')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
